// AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
// De enige plek die weet wat een afspraak is en wat een subtaak.
//
// WAAROM ÉÉN PLEK: een subtaak was tot 2026-09-30 geen eigen soort maar een
// huisbezoek (`Type: 'addressVisit'`) mét een `Parent`. Vijf afnemers van
// 'huisbezoek' vergaten dat onderscheid en telden subtaken mee als bezoeken —
// de portfoliokaart stond bij Woonbrigade Haarlem 24% te hoog, bij FIXbrigade
// Groningen 225%. Elke afnemer die zelf `Type === 'addressVisit'` vergelijkt,
// maakt die fout opnieuw. Vergelijk hier, nergens anders.
//
// WAAROM TWEE VORMEN: de omzetting naar het eigen soort (`addressSubtask`)
// draait pas ná de hosting-release, en de al uitgeleverde frontend schrijft
// tot die tijd de oude vorm. Deze helper leest allebei, zodat de code op elk
// punt in de uitrol klopt met wat er in de database staat.

/** Een huisbezoek: een afspraak bij een huishouden. */
const APPOINTMENT_TYPE = 'addressVisit'

/**
 * Een subtaak van een huishouden, sinds 2026-09-30 een eigen soort.
 * `Parent` is de optionele koppeling aan een afspraak: `''` = nog niet
 * ingepland, gevuld = ingepland. Nooit `null` — dat is in Firestore een echte
 * zoekwaarde (`Parent == null` vindt de afspraken zelf).
 * AI-CONTEXT: See docs/pitfalls.md#database-firestore-null-is-a-valid-query-value
 */
const SUBTASK_TYPE = 'addressSubtask'

/**
 * Een subtaak onder een taak die géén huisbezoek is (een roosterregel, een
 * meetmoment). Werk onder een taak, maar van geen enkel huishouden — en al
 * helemaal geen huisbezoek. Het oude schrijfpad stempelde hier 'addressVisit';
 * zo ontstonden 6 documenten die als huisbezoek meetelden.
 */
const GENERIC_SUBTASK_TYPE = 'subtask'

/**
 * @typedef {object} TaskLike
 * @property {string} [Type] - Soort van de taak (`Tasks.Type`)
 * @property {string|null} [Parent] - `ID` van de afspraak waar hij onder hangt; leeg of ontbrekend bij een afspraak zelf
 */

/**
 * Of dit een huisbezoek is: een afspraak, geen werk eronder.
 *
 * Een huisbezoek mét `Parent` is de oude vorm van een subtaak en telt dus NIET
 * als afspraak. Een document van het nieuwe soort zonder `Parent` is een nog
 * niet ingeplande subtaak — óók geen afspraak, hoe leeg dat veld ook is.
 *
 * @param {TaskLike|null|undefined} task
 * @returns {boolean}
 */
const isAppointment = (task) => task?.Type === APPOINTMENT_TYPE && !task.Parent

/**
 * Of dit een subtaak van een huishouden is, in de oude of de nieuwe vorm.
 *
 * Een taak van een ander soort met een `Parent` (een subtaak onder een
 * roosterregel) hoort niet bij een huishouden en telt hier niet mee.
 *
 * @param {TaskLike|null|undefined} task
 * @returns {boolean}
 */
const isSubtask = (task) => {
    if (!task) return false
    if (task.Type === SUBTASK_TYPE) return true
    return task.Type === APPOINTMENT_TYPE && Boolean(task.Parent)
}

export { APPOINTMENT_TYPE, SUBTASK_TYPE, GENERIC_SUBTASK_TYPE, isAppointment, isSubtask }
export default isAppointment
